import React, { useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import { css } from '@emotion/react';
import { DateTime } from 'luxon';
import { formatDates, formatDateTimes } from '../utility';
import { GatsbyImage } from 'gatsby-plugin-image';
import SEO from '../components/seo';
import GoogleMapReact from 'google-map-react';
import ConditionalWrapper from '../components/conditional-wrapper';
import PortableText from '../components/portable-text';
import Accordion from '../components/accordion';
import Clamp from '../components/clamp';
import Gallery from '../components/gallery';
import ProgramCard from '../components/program-card';
import Opportunity from '../components/opportunity';

export default function Page({data, location, setMenuAbsolute, setActivePage, setActiveSubPage, setShowLearn, setShowTeachers, setShowStudents, setShowExtra, setShowInfo, setImagesModal, showImages, setEmbed}) {
  const [type, setType] = useState(null);

  useEffect(() => {
    setMenuAbsolute(false);
    setActivePage('info');
    setActiveSubPage(data.page?.slug?.current);
    setShowExtra(false);
    setShowInfo(true);
    setImagesModal(false);

    if (location.pathname.startsWith('/teachers/')) {
      setShowLearn(true);
      setShowTeachers(true);
      setType('teachers');
    } else if (location.pathname.startsWith('/students/')) {
      setShowLearn(true);
      setShowStudents(true);
      setType('students');
    } else {
      setShowLearn(false);
      setShowTeachers(false);
      setShowStudents(false);
    }

    if (location.pathname.startsWith('/opportunities')) {
      setType('opportunities');
    }

    if (location.pathname.startsWith('/opportunity/')) {
      setActiveSubPage('opportunities');
      setType('opportunity');
    }
  }, []);

  function mapsLoaded(map, maps) {
    const marker = new maps.Marker({
      position: {
        lat: -37.8038617,
        lng: 144.9471569
      },
      map: map,
    });
  }

  return (
    <div
      css={css`
        margin-top: 6rem;
      `}
    >
      <SEO
        title={data.page?.title}
        image={(data.page?.featureImage) ? data.page?.featureImage.asset.gatsbyImageData.images.fallback?.srcSet.split(' ').reverse()[1].split('\n')[1] : null}
      />
      {data.page?.slug?.current === 'visit' && <div
        css={css`
          height: 65vh;
          margin-bottom: 4.5rem;
          filter: grayscale(100%);

          @media (max-width: 700px) {
            height: 90vw;
          }

          div {
            transition: none !important;
          }
        `}
      >
        <GoogleMapReact
          bootstrapURLKeys={{ key: 'AIzaSyC8oQ1hS7uOE3yclm8LXZg3DlUiq27rido' }}
          defaultCenter={{ lat: -37.8038617, lng: 144.9471569 }}
          defaultZoom={15}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => mapsLoaded(map, maps)}
        >
        </GoogleMapReact>
      </div>}
      {data.page?.featureImage && <figure
          css={css`
            position: relative;
            margin-bottom: 4.5rem;
            z-index: 1;
          `}
        >
          <GatsbyImage
            image={data.page.featureImage.asset.gatsbyImageData}
            loading="eager"
            alt={data.page.featureImage.asset.altText}
          />
        <figcaption className="type--smaller">{data.page.featureImage && data.page.featureImage.asset.description}</figcaption>
      </figure>}
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          margin-bottom: 2rem;

          p:last-of-type {
            margin-bottom: 3.6rem;
          }
        `}
      >
        <div
          css={css`
            grid-column: span 8;

            @media (max-width: 700px) {
              grid-column: span 12;
            }

            h1, h2 {
              margin: 0  0 3rem 0;
            }

            h1:last-of-type {
              margin-bottom: 6rem;
            }

            .indent {
              padding-left: 20rem;
            }
          `}
        >
          {type === 'opportunity' && <h1 className="type--large">{data.page?.title}</h1>}
          {data.page?._rawContent &&
            <PortableText
              css={css`
                h3 {
                  font-family: 'ROM Condensed', sans-serif;
                  font-weight: bold;
                  font-size: 6rem;
                  line-height: 1;
                  letter-spacing: -0.015em;
                  margin-bottom: 3rem;

                  @media (max-width: 700px) {
                    font-size: 4.5rem;
                  }
                }

                img {
                  width: 100%;
                }

                .indent {
                  padding-left: 20rem;
                }

                @media (max-width: 700px) {
                  margin-top: 7rem;
                }
              `}
              blocks={data.page._rawContent}
              setEmbed={setEmbed}
            />
          }
        </div>
        <div
          css={css`
            grid-column: span 4;

            @media (max-width: 700px) {
              grid-column: span 12;
            }

            p {
              margin-bottom: 1em;

              &:last-of-type {
                margin-bottom: 0;
              }
            }
          `}
        >
          <div
            css={css`
              position: sticky;
              top: var(--smallGutter);
            `}
          >
            {data.page?.information.length > 0 && <div
              css={css`
                border-top: 1px solid var(--black);
              `}
            >
              {data.page.information.map((information, i) =>
                <Accordion
                  key={information._key}
                  title={information.title}
                  open={(i === 0) ? true : false}
                >
                  <PortableText
                    blocks={information._rawContent}
                    setEmbed={setEmbed}
                  />
                </Accordion>
              )}
            </div>}
          </div>
        </div>
      </div>
      {(type === 'opportunities' || type === 'opportunity') && <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          padding-top: ${(type === 'opportunity') ? 0 : '5rem'};
          border-top: 1px solid var(--black);
          margin-top: ${(type === 'opportunity') ? '15.5rem' : '5rem'};
        `}
      >
        {type === 'opportunity' && <div
          className="type--medium"
          css={css`
            grid-column: span 12;
            margin-top: 0.7rem;
            margin-bottom: 6rem;
          `}
        >Other Opportunities</div>}
        {data.opportunities?.nodes.length > 0 && data.opportunities.nodes
          .filter((item, i) => item.slug?.current !== data.page.slug?.current)
          .sort((a, b) => {
            if (a.endDate < DateTime.now().toISODate()) {
              return 1;
            } else {
              return -1;
            }
          })
          .map((item, i) => (
            <Opportunity
              key={i}
              css={css`
                grid-column: span 4;

                @media (max-width: 900px) {
                  grid-column: span 6;
                }

                @media (max-width: 500px) {
                  grid-column: span 12;
                }
              `}
              title={item.title}
              slug={item.slug?.current}
              type={item.type}
              excerpt={item._rawShortDescription}
              endDate={item.endDate}
              setEmbed={setEmbed}
            />
          )
        )}
      </div>}
      {type === 'students' && <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          padding-top: 0.7rem;
          border-top: 1px solid var(--black);
          margin-top: 10rem;
        `}
      >
        <h3
          className="type--large"
          css={css`
            grid-column: span 8;
            margin-bottom: 13rem;

            @media (max-width: 900px) {
              grid-column: span 12;
              margin-bottom: 0;
            }

            a {
              text-decoration: none;
              color: var(--20Brown);

              &:hover {
                color: var(--pink);
              }
            }
          `}
        >Follow us on <a target="_blank" href="https://www.instagram.com/blindside_ari/">Instagram</a> or <a target="_blank" href="https://www.facebook.com/BLINDSIDEARI/">Facebook</a> to get updates on our exhibitions and events.</h3>
      </div>}
      {(type === 'teachers' || type === 'opportunity' || type === 'opportunities') && <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          padding-top: 0.7rem;
          border-top: 1px solid var(--black);
          margin-top: 10rem;
        `}
      >
        {type === 'teachers' && <h3
          className="type--large"
          css={css`
            grid-column: span 8;

            @media (max-width: 900px) {
              grid-column: span 12;
            }
          `}
        >Join our mailing list to receive updates about our Education Program.</h3>}
        {(type === 'opportunity' || type === 'opportunities') && <h3
          className="type--large"
          css={css`
            grid-column: span 8;

            @media (max-width: 900px) {
              grid-column: span 12;
            }
          `}
        >Subscribe now to hear about current opportunities at Blindside.</h3>}
        <form
          css={css`
            position: relative;
            grid-column-start: 4;
            grid-column-end: span 6;
            margin: 5rem 0;

            @media (max-width: 700px) {
              grid-column-start: 1;
              grid-column-end: span 12;
              margin: 0;
            }
          `}
          action="https://blindside.us2.list-manage.com/subscribe/post?u=9c9ccb60da895b0caf76a5b64&id=68fccc2582"
          method="POST"
        >
          <input
            css={css`
              width: 100%;
              background-color: transparent;
              padding: 1.4rem 2rem 1.2rem;
              border: 1px solid var(--black);
              border-radius: 4rem;
            `}
            type="email"
            name="EMAIL"
            placeholder="hello@example.com"
          />
          <input
            css={css`
              position: absolute;
              top: 1.5rem;
              right: 1.6rem;
              font-size: 2.8rem;
              background-color: transparent;
              border: none;
              cursor: pointer;
            `}
            type="submit"
            value="→"
          />
        </form>
      </div>}
    </div>
  );
}

export const query = graphql`
  query PageTemplateQuery($slug: String!) {
    page: sanityPage(slug: {current: {eq: $slug}}) {
      slug {
        current
      }
      title
      featureImage {
        ...Image
        asset {
          altText
          description
          gatsbyImageData(width: 1980, placeholder: NONE, layout: FULL_WIDTH)
        }
      }
      _rawContent(resolveReferences: {maxDepth: 10})
      information {
        _key
        title
        _rawContent(resolveReferences: {maxDepth: 10})
        open
      }
    }
  }
`
